import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { emailValidationPattern } from "src/schema/validations";

import { USER_TYPE } from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { useBooking } from "@hooks/useBooking/useBooking";
import { AddGuestsProps } from "@models/components/booking.model";
import { AddGuestSubmitType } from "@models/services/booking";
import {
  AddGuestType,
  CheckParticipantErrors,
  GuestFormData,
  GuestFormField,
  SelectGuestType,
} from "@models/shared/shared.model";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";
export const useAddGuests = ({ slotInfo }: AddGuestsProps) => {
  const ADDGUESTMODALTABS = useMemo(
    () => [
      { disabled: false, id: USER_TYPE.MEMBER, label: t("addGuest.members") },
      { disabled: false, id: USER_TYPE.GUEST, label: t("addGuest.guests") },
    ],
    []
  );

  const ADDGUESTMODALFIELDS: GuestFormField[] = [
    {
      labelName: t("firstName"),
      maxLength: 50,
      name: "firstName",
      type: "text",
    },
    { labelName: t("lastName"), maxLength: 50, name: "lastName", type: "text" },
    { labelName: t("mainEmail"), maxLength: 50, name: "email", type: "text" },
  ];
  const dispatch = useDispatch();
  const { getSelectedResource } = useBooking();

  const { checkparticipantErrors, guestList, selectedDate, selectedSlot } =
    useSelector((state: RootState) => state.booking);

  const [showAddGuestModal, setShowAddGuestModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(USER_TYPE.MEMBER);
  const [showMaxExceededMessage, setShowMaxExceededMessage] =
    useState<boolean>(false);
  const [selectedGuest, setSelectedGuest] = useState<SelectGuestType | null>(
    null
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addGuestError, setAddGuestError] = useState<string>("");
  const [guestMessage, setGuestMessage] = useState<string>("");
  const [guestFormData, setGuestFormData] = useState<GuestFormData>({
    email: "",
    firstName: "",
    lastName: "",
  });
  const [emailError, setEmailError] = useState<string>("");

  const prevSlotRef = useRef<string>("");

  const maxParticipants = selectedSlot?.slot?.max ?? null;
  const maxExceeded = useMemo(
    () => !!(maxParticipants && guestList.length >= maxParticipants),
    [maxParticipants, guestList.length]
  );

  const selectedSlotInfo = useMemo(
    () => getSelectedResource(slotInfo),
    [slotInfo, getSelectedResource]
  );

  const allowBringGuest = useMemo(
    () =>
      (selectedSlotInfo?.policyInfo?.allowBringGuest &&
        selectedSlotInfo?.slotInfo?.slots?.allowedGuestTimings.length > 0) ??
      false,
    [selectedSlotInfo]
  );

  const isGuestTimingAllowed = useMemo(
    () =>
      selectedSlotInfo?.slotInfo?.slots?.allowedGuestTimings?.includes(
        selectedSlot?.slot?.starts ?? ""
      ) ?? false,
    [selectedSlotInfo, selectedSlot]
  );

  const quotaExceeded = useMemo(() => {
    const quota = selectedSlotInfo?.quotaInfo?.MAX_GUEST_TIMES;
    return !!(quota && !quota.ignoreUG && quota.quotaTotal <= quota.quotaUsed);
  }, [selectedSlotInfo]);

  const isGuestAdditionDisabled = !(allowBringGuest && isGuestTimingAllowed);

  const resetGuestForm = useCallback(() => {
    setGuestFormData({ email: "", firstName: "", lastName: "" });
    setSelectedGuest(null);
    setAddGuestError("");
    setEmailError("");
    setShowMaxExceededMessage(false);
  }, []);

  const openAddGuestModal = useCallback(() => {
    setShowAddGuestModal(true);
    resetGuestForm();
  }, [resetGuestForm]);

  const closeAddGuestModal = useCallback(() => {
    setShowAddGuestModal(false);
    resetGuestForm();
  }, [resetGuestForm]);

  const loadOptions = useCallback(
    async (inputValue: string) => {
      if (!inputValue.trim()) return [];

      try {
        const response = await API_SERVICES.BOOKING.GET_GUEST_LIST(inputValue);
        return response.data
          .map((guest: SelectGuestType) => ({
            ...guest,
            name: commonUtils.getCardNameByType(guest),
          }))
          .filter(
            (guest: { profileUuid: string }) =>
              !guestList.some(p => p.profileUuid === guest.profileUuid)
          )
          .map((guest: { name: string; profileUuid: string }) => ({
            label: guest.name,
            value: guest.profileUuid,
            ...guest,
          }));
      } catch {
        return [];
      }
    },
    [guestList]
  );

  const buildGuestPayload = useCallback(
    (participantUuid: string | null): AddGuestSubmitType => ({
      bookingDate: selectedDate,
      bookingEndTime: selectedSlot?.slot?.ends ?? "",
      bookingStartTime: selectedSlot?.slot?.starts ?? "",
      currentParticipantArray: guestList.map(p => p.profileUuid),
      participantUuid: participantUuid ?? "",
      resourceUUID: selectedSlot?.resourceId ?? "",
    }),
    [selectedDate, selectedSlot, guestList]
  );

  const isGuestFormValid = useMemo(() => {
    const { email, firstName, lastName } = guestFormData;
    return (
      firstName.trim() !== "" && lastName.trim() !== "" && email.trim() !== ""
    );
  }, [guestFormData]);

  const mapCheckParticipantErrors = useCallback(
    (errors: CheckParticipantErrors[]) => {
      const result: Record<string, string> = {};
      for (const item of errors) {
        result[item?.participantUuid] = item?.error?.responseData?.quotaInfo
          ? item?.error?.responseData?.quotaInfo?.quotaMessage
          : item?.error?.message;
      }
      return result;
    },
    []
  );

  const checkParticipants = useCallback(
    async (participantUuid: string | null) => {
      setIsLoading(true);
      const postArray = buildGuestPayload(participantUuid);
      await API_SERVICES.BOOKING.CHECK_GUEST_LIST(postArray).then(response => {
        if (!response.data.status) {
          dispatch(
            bookingSlice.actions.setCheckParticipantErrors(
              mapCheckParticipantErrors(response?.data?.errors)
            )
          );
        } else {
          dispatch(bookingSlice.actions.setCheckParticipantErrors({}));
        }
        setIsLoading(false);
      });
    },
    [buildGuestPayload, dispatch]
  );

  const handleRemoveGuest = useCallback(
    (uuid: string) => {
      dispatch(bookingSlice.actions.removeGuest(uuid));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!selectedSlot?.slot) {
      setGuestMessage("");
      return;
    }

    const start = selectedSlot.slot.starts;
    const end = selectedSlot.slot.ends;
    const currentSlotKey = `${start}-${end}`;

    if (prevSlotRef.current === currentSlotKey) {
      return;
    }

    prevSlotRef.current = currentSlotKey;

    if (!selectedSlotInfo) {
      setGuestMessage("");
      return;
    }

    if (!allowBringGuest) {
      setGuestMessage(t("addGuest.guestRestrictionOnPolicy"));
      return;
    }

    if (!isGuestTimingAllowed) {
      const timings =
        selectedSlotInfo?.slotInfo?.slots?.allowedGuestTimings ?? [];

      setGuestMessage(
        t("addGuest.guestRestrictionOnTiming", {
          endTime: timings[timings.length - 1],
          startTime: timings[0],
        })
      );
      return;
    }

    if (quotaExceeded) {
      setGuestMessage(
        selectedSlotInfo?.quotaInfo?.MAX_GUEST_TIMES?.quotaMessage || ""
      );
      return;
    }
    setGuestMessage("");

    checkParticipants(null);
  }, [
    selectedSlot,
    selectedSlotInfo,
    allowBringGuest,
    isGuestTimingAllowed,
    quotaExceeded,
    checkParticipants,
  ]);

  const validateGuestForm = () => {
    const { email, firstName, lastName } = guestFormData;

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setAddGuestError(t("addGuest.enterAllRequiredFields"));
      return false;
    }

    if (!emailValidationPattern.test(email)) {
      setEmailError(t("addGuest.invalidEmail"));
      return false;
    }

    const emailExists = guestList.some(
      g => g.email?.trim().toLowerCase() === email.trim().toLowerCase()
    );

    if (emailExists) {
      setEmailError(t("addGuest.emailAlreadyExists"));
      return false;
    }

    return true;
  };

  const createExternalGuest = async () => {
    const response =
      await API_SERVICES.BOOKING.ADD_EXTERNAL_GUEST(guestFormData);

    if (!response.data.status) {
      setEmailError(response.data.message);
      return null;
    }

    return response.data.data; // new UUID
  };

  const addGuestToBooking = async (
    participantUuid: string,
    guestData: AddGuestType
  ) => {
    const payload = buildGuestPayload(participantUuid);

    try {
      const response = await API_SERVICES.BOOKING.ADD_GUEST(payload);

      if (response.data.status) {
        commonUtils.toastService(
          t(response.data.message),
          CONSTANTS.TOAST_TYPE.SUCCESS,
          true,
          "top-center"
        );

        dispatch(bookingSlice.actions.addGuest(guestData));
        setSelectedGuest(null);
        return true;
      }

      setAddGuestError(response.data.message);
      return false;
    } catch {
      return false;
    }
  };

  const buildMemberGuestData = () => ({
    companyName: selectedGuest?.companyName || "",
    contactType: selectedGuest?.contactType || USER_TYPE.MEMBER,
    email: selectedGuest?.email || "",
    firstName: selectedGuest?.firstName || "",
    lastName: selectedGuest?.lastName || "",
    profileUuid: selectedGuest?.value || "",
  });

  const buildExternalGuestData = (uuid: string): AddGuestType => {
    const { email, firstName, lastName } = guestFormData;

    return {
      companyName: "",
      contactType: USER_TYPE.GUEST,
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      profileUuid: uuid,
    };
  };

  const isAddGuestButtonDisabled = useMemo(() => {
    if (isLoading) return true;

    if (
      (activeTab === USER_TYPE.MEMBER && !selectedGuest) ||
      (activeTab === USER_TYPE.GUEST && !isGuestFormValid)
    )
      return true;

    return false;
  }, [isLoading, activeTab, selectedGuest, isGuestFormValid]);
  const handleAddGuest = useCallback(async () => {
    setAddGuestError("");
    setEmailError("");

    if (maxExceeded) {
      setShowMaxExceededMessage(true);
      return;
    }

    setIsLoading(true);

    try {
      // MEMBER TAB
      if (activeTab === USER_TYPE.MEMBER) {
        if (!selectedGuest) return;

        const memberData = buildMemberGuestData();
        await addGuestToBooking(memberData.profileUuid, memberData);
        return;
      }

      // GUEST TAB
      if (!validateGuestForm()) return;

      const newUuid = await createExternalGuest();
      if (!newUuid) return;

      const externalData = buildExternalGuestData(newUuid);
      const success = await addGuestToBooking(newUuid, externalData);

      if (success) {
        setGuestFormData({ email: "", firstName: "", lastName: "" });
      }
    } catch (err) {
      console.error("Unexpected error in handleAddGuest:", err);
    } finally {
      setIsLoading(false);
    }
  }, [
    activeTab,
    selectedGuest,
    maxExceeded,
    buildMemberGuestData,
    validateGuestForm,
    createExternalGuest,
    buildExternalGuestData,
    addGuestToBooking,
  ]);
  const actions = useMemo(
    () => ({
      closeAddGuestModal,
      handleAddGuest,
      handleRemoveGuest,
      loadOptions,
      openAddGuestModal,
    }),
    [
      closeAddGuestModal,
      handleAddGuest,
      handleRemoveGuest,
      loadOptions,
      openAddGuestModal,
    ]
  );

  const config = useMemo(
    () => ({
      ADDGUESTMODALFIELDS,
      ADDGUESTMODALTABS,
    }),
    [ADDGUESTMODALFIELDS, ADDGUESTMODALTABS]
  );

  const data = useMemo(
    () => ({
      allowBringGuest,
      checkparticipantErrors,
      guestFormData,
      guestList,
      isGuestAdditionDisabled,
      maxExceeded,
      maxParticipants,
      quotaExceeded,
      selectedGuest,
      selectedSlot,
    }),
    [
      allowBringGuest,
      checkparticipantErrors,
      guestFormData,
      guestList,
      isGuestAdditionDisabled,
      maxExceeded,
      maxParticipants,
      quotaExceeded,
      selectedGuest,
      selectedSlot,
    ]
  );

  const setters = useMemo(
    () => ({
      resetGuestForm,
      setActiveTab,
      setAddGuestError,
      setGuestFormData,
      setSelectedGuest,
    }),
    []
  );

  const ui = useMemo(
    () => ({
      activeTab,
      addGuestError,
      emailError,
      guestMessage,
      isAddGuestButtonDisabled,
      isLoading,
      showAddGuestModal,
      showMaxExceededMessage,
    }),
    [
      activeTab,
      addGuestError,
      emailError,
      guestMessage,
      isAddGuestButtonDisabled,
      isLoading,
      showAddGuestModal,
      showMaxExceededMessage,
    ]
  );
  return { actions, config, data, setters, ui };
};
